import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useDeleteSubscription } from 'src/features/subscriptions/api/useDeleteSubscription';
import { LIST_SUBSCRIPTIONS_RESOURCE } from 'src/features/subscriptions/api/useListSubscriptions';
import { Subscription } from 'src/features/subscriptions/utils/types';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { useNavigate } from 'react-router-dom';

interface DeleteSubscriptionFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSubscription: Subscription;
    children?: React.ReactNode;
}

function DeleteSubscriptionFormModal<TData>({
    selectedSubscription,
    children,
}: DeleteSubscriptionFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deleteSubscription } = useDeleteSubscription({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTIONS_RESOURCE] });
            navigate('/subscriptions');
            setIsModalOpen(false);
            toast({
                title: formatMessage(subscriptionsMessages.deleteSubscriptionSuccess),
                description: formatMessage(subscriptionsMessages.deleteSubscriptionSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...subscriptionsMessages.deleteSubscription },
                subTitle: {
                    ...subscriptionsMessages.deleteSubscriptionSubtitle,
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...subscriptionsMessages.confirmDeleteSubscription },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: subscriptionsMessages.deleteSubscription,
                callbackFn: () => {
                    return (
                        deleteSubscription({
                            id: selectedSubscription.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteSubscriptionFormModal;
